
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { MdSearch, MdInfo, MdCheckCircle, MdError, MdEmail, MdPerson, MdCalendarToday, MdArrowForward } from "react-icons/md";
import { createApi } from "../../../../core/api";
import { EmailLogListDto } from "../../../../core/api/types";
import { Dropdown } from "../../../components/SystemComponent/Dropdown";

const api = createApi();

const EmailLogsPage: React.FC = () => {
  const { setPageMenus } = useOutletContext<any>();
  const [logs, setLogs] = useState<EmailLogListDto[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50); // Grouped view might handle more items at once
  const [totalCount, setTotalCount] = useState(0);
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>(
    undefined
  );
  const [selectedLog, setSelectedLog] = useState<EmailLogListDto | null>(null);
  const [cachedUserInfo, setCachedUserInfo] = useState<Record<number, { name: string, email?: string }>>({});

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .user-card-premium:hover {
        transform: translateY(-4px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
      }
      .compact-card-premium:hover {
        background: #F8FAFC !important;
        border-color: #CBD5E1 !important;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.user.getAll();
      if ((res as any).ok) {
        const uList = (res as any).data?.data ?? (res as any).data ?? [];
        setUsers(Array.isArray(uList) ? uList : []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const payload: any = {
        page: page, // Swagger showed 0, but usually 1-indexed. We'll stick with 1.
        pageSize,
      };
      if (userIdFilter) {
        payload.userId = userIdFilter;
      }
      
      const res = await api.emailLog.getLogs(payload);
      // Based on provided JSON: { data: { items: [], totalCount: 3 ... } }
      if (res.ok && res.data) {
        const rootData = res.data as any;
        const innerData = rootData.data || {};
        const items = Array.isArray(innerData.items) ? [...innerData.items] : [];
        
        // Sort by sentDate descending (newest first)
        items.sort((a, b) => {
          const d1 = new Date(a.sentDate || 0).getTime();
          const d2 = new Date(b.sentDate || 0).getTime();
          return d2 - d1;
        });

        setLogs(items);
        setTotalCount(innerData.totalCount || items.length);
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error("Error loading email logs:", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, userIdFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Logs",
        items: [{ label: "Email Logs", route: "/dashboard/email-logs" }],
      },
    ]);
    loadLogs();
    return () => setPageMenus([]);
  }, [loadLogs, setPageMenus]);

  const handleClearFilter = () => {
    setUserIdFilter(undefined);
    setPage(1);
  };

  const groupedLogs = useMemo(() => {
    const groups: Record<number | string, any[]> = {};
    logs.forEach((log) => {
      const g = (log.toUserIds && log.toUserIds[0]) || log.userId || "system";
      if (!groups[g]) groups[g] = [];
      groups[g].push(log);
    });
    return groups;
  }, [logs]);

  const getUserInfo = (userId: any) => {
    if (userId === "system") return { name: "System / Transactional Logs", email: "system@internal" };
    const numericId = typeof userId === 'string' ? parseInt(userId) : userId;
    
    // Try local users state first
    const user = users.find((u) => u.id === numericId || u.user?.id === numericId);
    if (user) {
      const name = user.fullName || [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username || user.user?.fullName;
      const email = user.email || user.user?.email;
      if (name) return { name, email };
    }

    // Try cache
    if (numericId && cachedUserInfo[numericId]) return cachedUserInfo[numericId];

    // Trigger fetch if not found and numeric
    if (numericId && !isNaN(numericId) && !cachedUserInfo[numericId]) {
      api.user.getById(numericId).then(res => {
        if (res.ok && res.data) {
           const u = res.data.data || res.data;
           const name = u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
           const email = u.email;
           if (name) {
             setCachedUserInfo(prev => ({ ...prev, [numericId]: { name, email } }));
           }
        }
      });
    }

    return { name: `User #${numericId}`, email: "" };
  };

  const getUserName = (userId: any) => getUserInfo(userId).name;
  const getUserEmail = (userId: any) => getUserInfo(userId).email;

  const columns = [
    {
      key: "isSuccess",
      title: "Status",
      type: "boolean",
      minWidth: 100,
      accessor: (r: any) => r.isSuccess,
      render: (_: any, v: boolean) =>
        v ? (
          <Badge label="Success" icon={MdCheckCircle} color="#16A34A" bg="#DCFCE7" />
        ) : (
          <Badge label="Failed" icon={MdError} color="#DC2626" bg="#FEE2E2" />
        ),
    },
    {
      key: "sentDate",
      title: "Date",
      type: "date",
      minWidth: 160,
      accessor: (r: any) => r.sentDate,
      render: (_: any, v: any) => {
        const d = new Date(v);
        return (
          <div style={{ fontSize: 13, fontWeight: 500 }}>
             {d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })}
             <span style={{ marginLeft: 6, color: '#64748B', fontSize: 11 }}>{d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        );
      }
    },
    {
      key: "userId",
      title: "User",
      type: "text",
      minWidth: 200,
      accessor: (r: any) => {
        const id = (r.toUserIds && r.toUserIds[0]) || r.userId || "system";
        return getUserName(id);
      },
      render: (row: any) => {
        const id = (row.toUserIds && row.toUserIds[0]) || row.userId || "system";
        const info = getUserInfo(id);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ ...STYLING.miniAvatar, width: 28, height: 28, fontSize: 11, borderRadius: 8 }}>{info.name.charAt(0)}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{info.name}</div>
              {id !== 'system' && <div style={{ fontSize: 11, color: '#6366F1' }}>{info.email}</div>}
            </div>
          </div>
        )
      }
    },
    {
      key: "receiverEmail",
      title: "To",
      type: "text",
      minWidth: 180,
      accessor: (r: any) => r.receiverEmail,
      render: (_: any, v: string) => <div style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>{v}</div>
    },
    {
      key: "subject",
      title: "Subject",
      type: "text",
      minWidth: 250,
      accessor: (r: any) => r.subject,
      render: (_: any, v: string) => <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v || "(No Subject)"}</div>
    },
    {
      key: "templateCode",
      title: "Template",
      type: "text",
      minWidth: 140,
      accessor: (r: any) => r.templateCode,
      render: (_: any, v: string) => v ? (
        <div style={{ fontSize: 11, fontWeight: 700, background: '#F0F9FF', color: '#0369A1', padding: '2px 8px', borderRadius: 6, border: '1px solid #BAE6FD', display: 'inline-block' }}>{v}</div>
      ) : null
    },
    {
      key: "actions",
      title: "",
      filterable: false,
      minWidth: 60,
      render: (row: any) => (
        <div onClick={() => setSelectedLog(row)} style={{ cursor: 'pointer', color: '#6366F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MdInfo size={20} />
        </div>
      ),
    },
  ];

  return (
    <div style={STYLING.wrap} className="dashboard-page overflow-auto">
      <div style={STYLING.header}>
        <div>
          <div style={STYLING.title}>Email Logs</div>
          <div style={STYLING.subTitle}>Comprehensive history of all sent emails</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Dropdown
            items={[
              { label: "Show All Logs", value: undefined },
              ...users.map((u) => {
                const name = u.fullName || [u.firstName, u.lastName].filter(Boolean).join(" ") || u.username || u.email;
                return {
                  label: `${name} (${u.email || 'No email'})`,
                  value: u.id,
                };
              }),
            ]}
            value={userIdFilter}
            onSelect={(i) => {
              setUserIdFilter(i.value);
              setPage(1);
            }}
            searchable
            placeholder="Select a User to Filter..."
          />
          {userIdFilter !== undefined && (
            <Button variant="ghost" onClick={handleClearFilter}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <div style={STYLING.contentArea}>
        {loading ? (
          <div style={STYLING.statusMsg}>Loading logs...</div>
        ) : logs.length === 0 ? (
          <div style={STYLING.statusMsg}>No email logs found.</div>
        ) : (
          <>
            <div style={{ background: '#fff', borderRadius: 24, border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
              <Table
                tableKey="emailLogsTable"
                loading={loading}
                columns={columns}
                data={logs}
                features={{ sortable: true, filterable: true, pagination: false }}
              />
            </div>

            {/* Pagination Controls */}
            <div style={STYLING.pagination}>
              <div style={STYLING.pageInfo}>
                Total <strong>{totalCount}</strong> logs found
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button 
                  variant="ghost" 
                  disabled={page <= 1} 
                  onClick={() => { setPage(p => p - 1); window.scrollTo(0,0); }}
                >
                  Previous
                </Button>
                <div style={STYLING.pageBadge}>Page {page}</div>
                <Button 
                  variant="ghost" 
                  disabled={page * pageSize >= totalCount} 
                  onClick={() => { setPage(p => p + 1); window.scrollTo(0,0); }}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Modal */}
      <Modal open={!!selectedLog} onClose={() => setSelectedLog(null)}>
        <div style={STYLING.modalTitle}>Email Log Details</div>
        {selectedLog && (
          <div style={STYLING.details}>
            <div style={STYLING.detailRow}>
              <strong>Status:</strong>{" "}
              {selectedLog.isSuccess ? (
                <span style={{ color: "#22C55E" }}>Success</span>
              ) : (
                <span style={{ color: "#DC2626" }}>Failed</span>
              )}
            </div>
            <div style={STYLING.detailRow}>
              <strong>Sent Date:</strong>{" "}
              {selectedLog.sentDate ? new Date(selectedLog.sentDate).toLocaleString() : "n/a"}
            </div>
            <div style={STYLING.detailRow}>
              <strong>Template:</strong> {selectedLog.templateCode}
            </div>
            <div style={STYLING.detailRow}>
              <strong>To:</strong> {(selectedLog.toUserIds || []).map(id => getUserName(id)).join(", ")}
            </div>
            <div style={STYLING.detailRow}>
              <strong>Subject:</strong> {selectedLog.subject}
            </div>
            <div style={STYLING.detailRow}>
               <strong>Triggering Data:</strong>
               {(() => {
                 try {
                   if (!selectedLog.triggeringDataJson) return "n/a";
                   const parsed = JSON.parse(selectedLog.triggeringDataJson);
                   return (
                     <div style={{ background: '#F8FAFC', padding: 12, marginTop: 8, borderRadius: 12, border: '1px solid #E2E8F0', display: 'grid', gap: '8px' }}>
                       {Object.entries(parsed).map(([k, v]) => (
                         <div key={k} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', fontSize: 12 }}>
                           <span style={{ fontWeight: 700, color: '#64748B' }}>{k}:</span>
                           <span style={{ color: '#1E293B', wordBreak: 'break-all' }}>{String(v)}</span>
                         </div>
                       ))}
                     </div>
                   );
                 } catch (e) {
                   return (
                     <pre style={{ background: '#f8fafc', padding: 8, marginTop: 4, borderRadius: 4, fontSize: 11, overflowX: 'auto' }}>
                       {selectedLog.triggeringDataJson}
                     </pre>
                   );
                 }
               })()}
            </div>
            {!selectedLog.isSuccess && (
              <div style={STYLING.errorBox}>
                <strong>Error Message:</strong>
                <div>{selectedLog.errorMessage}</div>
              </div>
            )}
          </div>
        )}
        <div style={STYLING.actions}>
          <Button variant="ghost" onClick={() => setSelectedLog(null)}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const STYLING: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 0, padding: "32px 40px", background: "#F4F7FA", overflowY: "auto" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 32,
  },
  title: { fontSize: 26, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.02em" },
  subTitle: { fontSize: 14, color: "#64748B", marginTop: 4, fontWeight: 500 },
  contentArea: { display: "flex", flexDirection: "column", gap: 32, paddingBottom: 120 },
  logsGrid: { display: "grid", gridTemplateColumns: "1fr", gap: 20 },
  logCard: { 
    background: "#fff", 
    borderRadius: 24, 
    border: "1px solid #E2E8F0", 
    display: "flex", 
    overflow: "hidden", 
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: 'pointer',
    position: 'relative'
  },
  statusIndicator: { width: 6 },
  logCardContent: { flex: 1, padding: "20px 24px" },
  logCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  userBrief: { display: "flex", alignItems: "center", gap: 12 },
  miniAvatar: { width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 },
  userName: { fontSize: 15, fontWeight: 700, color: "#1E293B" },
  userEmail: { fontSize: 12, color: "#6366F1", fontWeight: 500 },
  logDate: { fontSize: 12, color: "#94A3B8", display: "flex", alignItems: "center", background: "#F8FAFC", padding: "6px 12px", borderRadius: 10, border: '1px solid #F1F5F9' },
  logBody: { marginBottom: 16 },
  logSubject: { fontSize: 16, fontWeight: 800, color: "#0F172A", marginBottom: 4, display: 'flex', alignItems: 'center' },
  logRecipient: { fontSize: 13, color: "#64748B", fontWeight: 500 },
  logCardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16, borderTop: "1px solid #F1F5F9" },
  templateBadge: { fontSize: 11, fontWeight: 700, background: '#EFF6FF', color: '#1E40AF', padding: '4px 10px', borderRadius: 8, border: '1px solid #DBEAFE' },
  moreBtn: { fontSize: 13, fontWeight: 700, color: '#6366F1', display: 'flex', alignItems: 'center', gap: 4 },
  showMoreHint: { padding: "10px 14px", textAlign: "center", fontSize: 11, color: "#94A3B8", fontStyle: "italic", fontWeight: 500 },
  statusMsg: {
    padding: 120,
    textAlign: "center",
    color: "#64748B",
    fontSize: 18,
    background: "#fff",
    borderRadius: 32,
    border: "2px dashed #E2E8F0",
    fontWeight: 500
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 32px",
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(8px)",
    borderRadius: 24,
    border: "1px solid #E2E8F0",
    position: "sticky",
    bottom: 0,
    marginTop: -16, // Pull it closer to table or match gap
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.02)",
    zIndex: 10
  },
  pageInfo: { fontSize: 14, color: "#475569", fontWeight: 500 },
  pageBadge: { 
    background: "#EEF2FF", 
    border: "1px solid #E0E7FF", 
    borderRadius: 10, 
    padding: "6px 16px", 
    display: "flex", 
    alignItems: "center", 
    fontSize: 14, 
    fontWeight: 700, 
    color: "#4F46E5" 
  },
  modalTitle: { fontSize: 22, fontWeight: 900, marginBottom: 24, color: "#0F172A" },
  details: { display: "flex", flexDirection: "column", gap: 16 },
  detailRow: { fontSize: 14, color: "#334155", fontWeight: 500 },
  errorBox: {
    background: "#FFF1F2",
    padding: 20,
    borderRadius: 16,
    border: "1px solid #FFE4E6",
    fontSize: 13,
    color: "#E11D48",
    marginTop: 12,
    fontWeight: 500
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 32,
  }
};

export default EmailLogsPage;
